/**
 * ClientInsuranceDialog - Manages client insurance policies
 * 
 * A self-contained component that handles:
 * - Viewing/listing client's insurance policies
 * - Adding new insurance policies
 * - Editing existing insurance policies
 * - Archiving/reinstating insurance policies
 * - Viewing insurance card images
 * - Drag-and-drop priority reordering
 * 
 * Extracted from App.js to reduce monolith size
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { DebouncedInput } from '../../components/ui/debounced-textarea';
import { InsuranceCompanyOptions } from './InsuranceCompanyOptions';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Plus, 
  Edit, 
  Archive, 
  RefreshCw, 
  CreditCard, 
  GripVertical, 
  ChevronUp, 
  ChevronDown,
  AlertCircle,
  Phone,
  Copy,
  Trash2
} from 'lucide-react';
import { InsuranceCardViewer } from './InsuranceCardViewer';
import { EligibilityBadge } from '../../utils/eligibilityDisplay';
import {
  fmtLocalDate,
  isInsuranceExpired,
  isInsuranceEndingSoon,
} from '../../utils/insuranceDates';
import { AlertTriangle, History } from 'lucide-react';
import ManualVerifyEligibilityDialog from './ManualVerifyEligibilityDialog';

const API = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────────
// Click-to-copy Member ID — billers frequently need to paste the
// payer's member ID into external portals (Availity portal, payer
// websites, BCBS provider tools).  Clicking on the value copies it
// to the clipboard and shows a toast so they don't have to
// triple-click+ctrl+c.
// ─────────────────────────────────────────────────────────────────
const CopyableMemberId = ({ value, testIdSuffix = '' }) => {
  if (!value) return null;
  const handleCopy = (e) => {
    e.stopPropagation();
    try {
      navigator.clipboard.writeText(String(value));
      toast.success(`Member ID copied: ${value}`);
    } catch (err) {
      toast.error('Could not copy to clipboard');
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      data-testid={`copy-member-id-${testIdSuffix}`}
      title="Click to copy Member ID"
      className="inline-flex items-center gap-1 font-mono hover:bg-blue-50 hover:text-blue-700 rounded px-1 -mx-1 transition-colors group cursor-copy"
    >
      <span>{value}</span>
      <Copy className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
};

// ─────────────────────────────────────────────────────────────────
// Reusable Notes panel (May 2026 — added so billers can record
// payer-specific scratch notes without polluting the clinical chart).
//
// `scope` decides the base URL:
//   - "insurance"        → /clients/{id}/insurances/{insId}/notes/...
//   - "client_insurance" → /clients/{id}/insurance-notes/...
//
// Edit/delete are author-or-admin gated server-side; the frontend
// hides the buttons when the active user lacks permission so the
// UI never lies about what an action will do.
// ─────────────────────────────────────────────────────────────────
function NotesPanelBase({ baseUrl, title, initialNotes = [], onChanged, emptyHint, currentUser }) {
  const [notes, setNotes] = useState(initialNotes);
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setNotes(initialNotes || []); }, [initialNotes]);

  const canEdit = (note) => {
    if (!currentUser) return true; // optimistic — server is the source of truth
    if (currentUser.role === 'super_admin' || currentUser.role === 'admin' || currentUser.is_billing_manager) return true;
    return (note.author_id || '') === (currentUser.id || '');
  };

  const refresh = async () => {
    onChanged?.();
  };

  const add = async () => {
    const content = draft.trim();
    if (!content || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api${baseUrl}`, { content });
      const next = [...notes, r.data.note];
      setNotes(next);
      setDraft('');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add note');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (n) => { setEditingId(n.id); setEditingContent(n.content || ''); };
  const cancelEdit = () => { setEditingId(null); setEditingContent(''); };
  const saveEdit = async () => {
    const content = editingContent.trim();
    if (!content || !editingId || busy) return;
    setBusy(true);
    try {
      const r = await axios.put(`${API}/api${baseUrl}/${editingId}`, { content });
      setNotes(notes.map((n) => (n.id === editingId ? r.data.note : n)));
      cancelEdit();
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update note');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (n) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/api${baseUrl}/${n.id}`);
      setNotes(notes.filter((x) => x.id !== n.id));
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete note');
    } finally {
      setBusy(false);
    }
  };

  const fmtTs = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-2 border-t pt-4" data-testid={`notes-panel-${baseUrl.replace(/\//g, '-')}`}>
      <h4 className="font-semibold">{title}</h4>
      {notes.length === 0 && (
        <p className="text-xs text-gray-500">{emptyHint || 'No notes yet.'}</p>
      )}
      {notes.length > 0 && (
        <ul className="space-y-2">
          {notes.map((n) => (
            <li key={n.id} className="bg-gray-50 border border-gray-200 rounded p-2" data-testid={`note-${n.id}`}>
              <div className="flex items-center justify-between text-[11px] text-gray-500">
                <span><strong className="text-gray-700">{n.author_name || 'Unknown'}</strong> · {fmtTs(n.created_at)}{n.updated_at ? ' (edited)' : ''}{n.scope_label ? ` · ${n.scope_label}` : ''}</span>
                {canEdit(n) && editingId !== n.id && (
                  <span className="flex gap-2">
                    <button className="text-blue-600 hover:underline" onClick={() => startEdit(n)} data-testid={`note-edit-${n.id}`}>Edit</button>
                    <button className="text-red-600 hover:underline" onClick={() => remove(n)} data-testid={`note-delete-${n.id}`}>Delete</button>
                  </span>
                )}
              </div>
              {editingId === n.id ? (
                <div className="mt-1 space-y-1">
                  <textarea
                    className="w-full text-sm border border-gray-300 rounded p-1"
                    rows={2}
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                    data-testid={`note-edit-textarea-${n.id}`}
                  />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveEdit} disabled={busy} data-testid={`note-save-${n.id}`}>Save</Button>
                    <Button size="sm" variant="outline" onClick={cancelEdit} data-testid={`note-cancel-${n.id}`}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-800 whitespace-pre-wrap mt-1">{n.content}</p>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="flex gap-2 pt-1">
        <textarea
          className="flex-1 text-sm border border-gray-300 rounded p-2"
          rows={2}
          placeholder="Add a note…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          data-testid="note-draft-textarea"
        />
        <Button onClick={add} disabled={!draft.trim() || busy} data-testid="note-add-btn">Add</Button>
      </div>
    </div>
  );
}

function InsuranceNotesPanel({ clientId, insuranceId, initialNotes, onChanged }) {
  // Current user lookup is best-effort; the backend always enforces auth.
  let currentUser = null;
  try {
    const raw = localStorage.getItem('user');
    if (raw) currentUser = JSON.parse(raw);
  } catch { /* noop */ }
  return (
    <NotesPanelBase
      baseUrl={`/clients/${clientId}/insurances/${insuranceId}/notes`}
      title="Policy Notes"
      initialNotes={initialNotes}
      onChanged={onChanged}
      emptyHint="No notes on this policy yet. Use this for payer-specific scratch (call logs, auth #s, member-services tips)."
      currentUser={currentUser}
    />
  );
}

function ClientInsuranceNotesPanel({ clientId, onChanged }) {
  const [notes, setNotes] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!clientId) return undefined;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/clients/${clientId}/insurance-notes`);
        if (!cancelled) setNotes(r.data?.notes || []);
      } catch (e) {
        if (!cancelled) console.warn('Failed to load client insurance notes', e);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);
  let currentUser = null;
  try {
    const raw = localStorage.getItem('user');
    if (raw) currentUser = JSON.parse(raw);
  } catch { /* noop */ }
  return (
    <NotesPanelBase
      baseUrl={`/clients/${clientId}/insurance-notes`}
      title="Client-Wide Insurance Notes"
      initialNotes={notes}
      onChanged={() => { onChanged?.(); }}
      emptyHint="No client-wide insurance notes. Use these for cross-policy observations (appeals, member-services preferences, etc.)."
      currentUser={currentUser}
    />
  );
}

export function ClientInsuranceDialog({
  isOpen,
  onClose,
  client,
  insuranceCompanies = [],
  onUpdate,
  onEligibilityCheck,
  /**
   * Optional initial values to prefill the "Add Insurance" form.
   * Used when the user clicks "Add to chart" on a 271's "Other Active
   * Coverage" entry — we hand the dialog the parsed payer name,
   * member ID, group #, and effective dates so the biller doesn't
   * have to retype anything.  Shape is the same as
   * `insuranceFormState`; missing keys fall through to defaults.
   * Cleared via `onPrefillConsumed` once applied so re-opening the
   * dialog later doesn't keep stamping the same values in.
   */
  prefillData = null,
  onPrefillConsumed,
  /**
   * Jun 2026 — when true, saving (add or edit) first shows a native
   * confirmation naming the client, so a biller working from a list
   * surface (e.g. the Eligibility tab pill) can't accidentally write
   * insurance changes to the wrong chart.
   */
  confirmBeforeSave = false,
}) {
  // Main dialog state
  const [clientInsurances, setClientInsurances] = useState([]);
  const [showArchivedInsurances, setShowArchivedInsurances] = useState(false);
  // Per-card "View history" affordance (June 2026 — closes the loop
  // on archive/reinstate-preserves-end_date by letting billers audit
  // exactly what happened to a policy over time).  Holds the
  // insurance object whose `history` array should be shown, or null
  // when the side-sheet is closed.
  const [historyForInsurance, setHistoryForInsurance] = useState(null);
  // Manual eligibility verification dialog state (May 29 2026)
  const [manualVerifyOpen, setManualVerifyOpen] = useState(false);
  const [manualVerifyTarget, setManualVerifyTarget] = useState(null);
  const [draggedInsurance, setDraggedInsurance] = useState(null);
  
  // Form dialog state
  const [showInsuranceForm, setShowInsuranceForm] = useState(false);
  const [editingClientInsurance, setEditingClientInsurance] = useState(null);
  const [selectedInsuranceCompany, setSelectedInsuranceCompany] = useState(null);
  const [insuranceSearchTerm, setInsuranceSearchTerm] = useState('');
  const [insuranceFormState, setInsuranceFormState] = useState({
    company: '',
    plan: '',
    member_id: '',
    group_number: '',
    phone: '',
    relationship_to_insured: 'self',
    insured_name: '',
    insured_ssn: '',
    insured_dob: '',
    card_front_url: '',
    card_back_url: '',
    copay_amount: '',
    deductible_amount: '',
    priority: 1,
    start_date: '',
    end_date: '',
    eligibility_status: 'pending',
    msp_type_code: '12',
    alternate_ids: [],  // [{id?, label, value}] — see InsuranceAlternateId
  });
  
  // Card view dialog state
  const [showInsuranceCardView, setShowInsuranceCardView] = useState(false);
  const [viewingInsuranceCards, setViewingInsuranceCards] = useState(null);

  // Rank-cascade confirmation (Jun 2026) — {shifts: [{id, company, from, to}]}
  const [cascadePrompt, setCascadePrompt] = useState(null);

  // Local state for insurance companies (fetched when dialog opens)
  const [localInsuranceCompanies, setLocalInsuranceCompanies] = useState([]);

  // Track which client's data is currently in `clientInsurances` so we can
  // hide stale rows during the moment between mount and fetch resolution
  // (otherwise switching from client A → client B briefly shows A's policies).
  const [loadedForClientId, setLoadedForClientId] = useState(null);
  const [isLoadingInsurances, setIsLoadingInsurances] = useState(false);

  // Fetch client insurances AND insurance companies when dialog opens
  useEffect(() => {
    if (isOpen && client?.id) {
      // Clear stale data from a previously-viewed client immediately so
      // the UI never flashes another client's policies while we re-fetch.
      if (loadedForClientId !== client.id) {
        setClientInsurances([]);
      }
      fetchClientInsurances();
      // Only fetch insurance companies if not passed as prop
      if (!insuranceCompanies || insuranceCompanies.length === 0) {
        fetchInsuranceCompanies();
      }
    }
  // Depend on length (not array identity): callers that omit the prop
  // get a fresh `[]` default each render, which previously re-ran this
  // effect infinitely (fetch → setState → render → new [] → fetch …).
  // eslint-disable-next-line
  }, [isOpen, client?.id, insuranceCompanies?.length]);

  // ── Prefill from a 271 "Other Active Coverage" row ──────────────
  // When the parent passes `prefillData` and the dialog is open, drop
  // straight into the Add-Insurance form with the payer's values
  // already filled in (name / member ID / group / effective dates).
  // Then notify the parent so it can clear the prefill — without
  // that, the form would re-prefill every time the user re-opens
  // the dialog.
  useEffect(() => {
    if (!isOpen || !prefillData) return;
    // Wait until insurances have loaded so the auto-priority math is
    // right (otherwise a brand-new "Add" action could pin priority=1
    // even when the chart already has primary coverage).
    if (loadedForClientId !== client?.id) return;

    const activeInsurances = clientInsurances.filter(ins => ins.status !== 'archived');
    const nextPriority = activeInsurances.length > 0
      ? Math.max(...activeInsurances.map(ins => ins.priority || 1)) + 1
      : 1;

    setEditingClientInsurance(null);
    // Best-effort lookup against the org's master insurance list so the
    // dropdown shows the matched company (instead of the user having
    // to re-pick).  Match on case-insensitive substring of the payer
    // name we got back from the 271 — payer names are messy.
    const wanted = (prefillData.company || '').toLowerCase().trim();
    const matched = wanted && availableInsuranceCompanies.find(c =>
      (c.name || '').toLowerCase().includes(wanted) ||
      wanted.includes((c.name || '').toLowerCase())
    );
    setSelectedInsuranceCompany(matched || null);

    setInsuranceFormState({
      company: matched?.name || prefillData.company || '',
      plan: prefillData.plan || '',
      member_id: prefillData.member_id || '',
      group_number: prefillData.group_number || '',
      phone: prefillData.phone || '',
      relationship_to_insured: prefillData.relationship_to_insured || 'self',
      insured_name: prefillData.insured_name || '',
      insured_ssn: prefillData.insured_ssn || '',
      insured_dob: prefillData.insured_dob || '',
      card_front_url: '',
      card_back_url: '',
      copay_amount: '',
      deductible_amount: '',
      priority: prefillData.priority || nextPriority,
      start_date: prefillData.start_date || '',
      end_date: prefillData.end_date || '',
      eligibility_status: 'pending',
      msp_type_code: prefillData.msp_type_code || '12',
      alternate_ids: [],
    });
    setShowInsuranceForm(true);
    onPrefillConsumed?.();
  // eslint-disable-next-line
  }, [isOpen, prefillData, loadedForClientId, client?.id]);

  // When the dialog closes, dump local state so the next open starts clean.
  useEffect(() => {
    if (!isOpen) {
      setClientInsurances([]);
      setLoadedForClientId(null);
      setShowInsuranceForm(false);
      setShowInsuranceCardView(false);
    }
  }, [isOpen]);

  const fetchClientInsurances = async () => {
    if (!client?.id) return;
    const targetClientId = client.id;
    setIsLoadingInsurances(true);
    try {
      const response = await axios.get(`${API}/api/clients/${targetClientId}/insurances?include_archived=true`);
      // If the user switched clients again while this request was in
      // flight, drop the response — a newer fetch will set the right data.
      if (client?.id !== targetClientId) return;
      setClientInsurances(response.data);
      setLoadedForClientId(targetClientId);
    } catch (error) {
      console.error('Error fetching insurances:', error);
      toast.error('Failed to load insurances');
    } finally {
      setIsLoadingInsurances(false);
    }
  };
  
  const fetchInsuranceCompanies = async () => {
    try {
      const response = await axios.get(`${API}/api/insurances`);
      setLocalInsuranceCompanies(response.data);
    } catch (error) {
      console.error('Error fetching insurance companies:', error);
    }
  };
  
  // Use passed insuranceCompanies if available, otherwise use locally fetched
  const availableInsuranceCompanies = (insuranceCompanies && insuranceCompanies.length > 0) 
    ? insuranceCompanies 
    : localInsuranceCompanies;

  // Handler: Open add insurance form
  const handleAddInsuranceClick = () => {
    setEditingClientInsurance(null);
    setSelectedInsuranceCompany(null);
    
    // Auto-calculate next priority based on existing active insurances
    const activeInsurances = clientInsurances.filter(ins => ins.status !== 'archived');
    const nextPriority = activeInsurances.length > 0 
      ? Math.max(...activeInsurances.map(ins => ins.priority || 1)) + 1 
      : 1;
    
    setInsuranceFormState({
      company: '',
      plan: '',
      member_id: '',
      group_number: '',
      phone: '',
      relationship_to_insured: 'self',
      insured_name: '',
      insured_ssn: '',
      insured_dob: '',
      card_front_url: '',
      card_back_url: '',
      copay_amount: '',
      deductible_amount: '',
      priority: nextPriority, // Auto-set to secondary/tertiary if primary exists
      start_date: '',
      end_date: '',
      eligibility_status: 'pending',
      msp_type_code: '12',
      alternate_ids: [],
    });
    setShowInsuranceForm(true);
  };

  // Handler: Open edit insurance form
  const handleEditInsuranceClick = (insurance) => {
    setEditingClientInsurance(insurance);
    const insuranceCompany = availableInsuranceCompanies.find(ins => ins.name === insurance.company);
    setSelectedInsuranceCompany(insuranceCompany);
    setInsuranceFormState({
      company: insurance.company || '',
      plan: insurance.plan || '',
      member_id: insurance.member_id || '',
      group_number: insurance.group_number || '',
      phone: insurance.phone || '',
      relationship_to_insured: insurance.relationship_to_insured || 'self',
      insured_name: insurance.insured_name || '',
      insured_ssn: insurance.insured_ssn || '',
      insured_dob: insurance.insured_dob || '',
      card_front_url: insurance.card_front_url || '',
      card_back_url: insurance.card_back_url || '',
      copay_amount: insurance.copay_amount || '',
      deductible_amount: insurance.deductible_amount || '',
      priority: insurance.priority || 1,
      start_date: insurance.start_date || '',
      end_date: insurance.end_date || '',
      eligibility_status: insurance.eligibility_status || 'pending',
      msp_type_code: insurance.msp_type_code || '12',
      alternate_ids: Array.isArray(insurance.alternate_ids) ? insurance.alternate_ids : [],
    });
    setShowInsuranceForm(true);
  };

  // Handler: Save insurance (add or update)
  //
  // Jun 2026 — optimistic.  Earlier flow:
  //   PUT  →  await refetch  →  setState  →  close dialog  →  parent refetch
  // ~600-900ms before the user saw their change reflected on the
  // client overview.  Now:
  //   1) snapshot pre-edit state for rollback,
  //   2) close dialog immediately,
  //   3) update local insurance list optimistically,
  //   4) call onUpdate(optimistic) so parent (`handleInsuranceUpdate`)
  //      can paint the overview / client-list cards without waiting,
  //   5) fire the PUT/POST; on success refresh from server response,
  //      on failure restore the snapshot and surface the error.
  const handleSaveClientInsurance = async () => {
    if (!client) return;
    if (!insuranceFormState.company) {
      toast.error('Insurance Company is required');
      return;
    }
    if (!insuranceFormState.member_id) {
      toast.error('Member ID is required');
      return;
    }

    if (confirmBeforeSave) {
      const clientName = client.full_name || client.preferred_name || 'this client';
      const verb = editingClientInsurance ? 'update' : 'add';
      const ok = window.confirm(
        `You are about to ${verb} insurance for ${clientName}. This will change ${clientName}'s chart. Continue?`
      );
      if (!ok) return;
    }

    // Jun 2026 — rank-collision detection.  If the chosen priority is
    // already held by another active policy, ask whether to cascade
    // (Primary→Secondary, Secondary→Tertiary, ...) before saving.
    const shifts = computeCascadeShifts();
    if (shifts.length > 0) {
      setCascadePrompt({ shifts });
      return;
    }
    await performSaveClientInsurance(false);
  };

  // Mirrors the backend chain-shift: shift ranks down starting at the
  // desired priority, following collisions (non-contiguous ranks stop).
  const computeCascadeShifts = () => {
    const desired = parseInt(insuranceFormState.priority) || 1;
    const activeOthers = clientInsurances.filter(
      (ins) => ins.status !== 'archived' && ins.id !== editingClientInsurance?.id
    );
    if (!activeOthers.some((ins) => (parseInt(ins.priority) || 1) === desired)) return [];
    const shifts = [];
    const done = new Set();
    let rank = desired;
    for (;;) {
      const rows = activeOthers.filter(
        (ins) => !done.has(ins.id) && (parseInt(ins.priority) || 1) === rank
      );
      if (!rows.length) break;
      rows.forEach((r) => {
        shifts.push({ id: r.id, company: r.company, from: rank, to: rank + 1 });
        done.add(r.id);
      });
      rank += 1;
    }
    return shifts;
  };

  const performSaveClientInsurance = async (cascade) => {
    const cascadeShifts = cascade ? (cascadePrompt?.shifts || []) : [];
    setCascadePrompt(null);

    const isUpdate = !!editingClientInsurance;
    const previousInsurances = clientInsurances;
    const tempId = editingClientInsurance?.id || `temp-${Date.now()}`;
    const optimisticInsurance = {
      ...(editingClientInsurance || {}),
      ...insuranceFormState,
      id: tempId,
      _optimistic: true,
    };
    const shiftedById = new Map(cascadeShifts.map((s) => [s.id, s.to]));
    const basedList = previousInsurances.map((ins) =>
      shiftedById.has(ins.id) ? { ...ins, priority: shiftedById.get(ins.id) } : ins
    );
    const optimisticList = isUpdate
      ? basedList.map((ins) => (ins.id === tempId ? optimisticInsurance : ins))
      : [...basedList, optimisticInsurance];

    setClientInsurances(optimisticList);
    setShowInsuranceForm(false);
    toast.success(isUpdate ? 'Insurance updated' : 'Insurance added');
    if (onUpdate) onUpdate(optimisticList);

    const payload = cascade
      ? { ...insuranceFormState, cascade_priorities: true }
      : insuranceFormState;

    try {
      if (isUpdate) {
        await axios.put(
          `${API}/api/clients/${client.id}/insurances/${editingClientInsurance.id}`,
          payload,
        );
      } else {
        await axios.post(
          `${API}/api/clients/${client.id}/insurances`,
          payload,
        );
      }
      // Refresh to pick up server-side fields (id for new rows, updated
      // timestamps, derived flags like ``is_active``).  The parent
      // ``handleInsuranceUpdate`` then re-runs with the authoritative
      // server payload, replacing the optimistic row.
      await fetchClientInsurances();
      if (onUpdate) onUpdate();
    } catch (error) {
      // Roll back local state and ask parent to do the same.
      setClientInsurances(previousInsurances);
      if (onUpdate) onUpdate(previousInsurances);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to save insurance';
      if (errorMsg.includes('Field required')) {
        toast.error('Please fill in all required fields (Insurance Company and Member ID)');
      } else {
        toast.error(errorMsg);
      }
      console.error('Error saving insurance:', error);
    }
  };

  // Handler: Delete insurance (hard-delete; only when no usage).
  // Backend returns 409 + structured `usage` payload when the row
  // has been billed / authorized / verified — we then prompt the
  // user to Archive instead.
  const [deleteCandidate, setDeleteCandidate] = useState(null); // {insurance, usage?}

  const handleDeleteInsurance = async (insurance) => {
    try {
      await axios.delete(
        `${API}/api/clients/${client.id}/insurances/${insurance.id}`
      );
      toast.success('Insurance deleted');
      setDeleteCandidate(null);
      await fetchClientInsurances();
      if (onUpdate) onUpdate();
    } catch (error) {
      // 409 → has usage; show "archive instead" prompt
      if (error?.response?.status === 409) {
        const detail = error.response.data?.detail || {};
        setDeleteCandidate({ insurance, usage: detail.usage || {} });
        return;
      }
      // 403 → role mismatch
      if (error?.response?.status === 403) {
        toast.error('Only admin or office staff can delete insurance profiles');
        setDeleteCandidate(null);
        return;
      }
      console.error('Error deleting insurance:', error);
      toast.error('Failed to delete insurance');
      setDeleteCandidate(null);
    }
  };

  // Handler: Archive insurance
  const handleArchiveInsurance = async (insurance) => {
    try {
      await axios.post(
        `${API}/api/clients/${client.id}/insurances/${insurance.id}/archive`,
        { archive_reason: 'Manually archived', auto_promote: true }
      );
      toast.success('Insurance archived');
      await fetchClientInsurances();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error archiving insurance:', error);
      toast.error('Failed to archive insurance');
    }
  };

  // Handler: Reinstate insurance
  const handleReinstateInsurance = async (insurance) => {
    try {
      await axios.post(
        `${API}/api/clients/${client.id}/insurances/${insurance.id}/reinstate`,
        { new_priority: 1 }
      );
      toast.success('Insurance reinstated');
      await fetchClientInsurances();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error reinstating insurance:', error);
      toast.error('Failed to reinstate insurance');
    }
  };

  // Handler: Upload insurance card file
  const handleUploadInsuranceCardFile = async (side, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('insurance_type', 'client_insurance');
      formData.append('side', side);

      const response = await axios.post(
        `${API}/api/clients/${client.id}/insurance-card`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const newCardUrl = response.data.file_path;
      
      // Update local state
      setInsuranceFormState(prev => ({
        ...prev,
        [`card_${side}_url`]: newCardUrl
      }));

      // If editing an existing insurance, immediately persist the card URL
      if (editingClientInsurance) {
        try {
          await axios.put(
            `${API}/api/clients/${client.id}/insurances/${editingClientInsurance.id}`,
            { [`card_${side}_url`]: newCardUrl }
          );
          await fetchClientInsurances();
        } catch (updateError) {
          console.error('Error auto-saving card URL:', updateError);
        }
      }

      toast.success(`Card ${side} uploaded successfully`);
    } catch (error) {
      console.error('Error uploading card:', error);
      toast.error(`Failed to upload card ${side}`);
    }
  };

  // Drag and drop handlers
  const handleInsuranceDragStart = (e, insurance) => {
    setDraggedInsurance(insurance);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleInsuranceDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleInsuranceDrop = async (e, targetInsurance) => {
    e.preventDefault();
    
    if (!draggedInsurance || draggedInsurance.id === targetInsurance.id) {
      setDraggedInsurance(null);
      return;
    }

    try {
      await axios.put(
        `${API}/api/clients/${client.id}/insurances/${draggedInsurance.id}/priority`,
        { new_priority: targetInsurance.priority }
      );

      toast.success('Insurance priority updated');
      await fetchClientInsurances();
      setDraggedInsurance(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating priority:', error);
      toast.error('Failed to update priority');
      setDraggedInsurance(null);
    }
  };

  // Priority labels and colors
  const priorityLabels = {1: 'Primary', 2: 'Secondary', 3: 'Tertiary', 4: 'Quaternary'};
  const priorityColors = {
    1: 'bg-blue-50 border-blue-200',
    2: 'bg-green-50 border-green-200',
    3: 'bg-purple-50 border-purple-200',
    4: 'bg-orange-50 border-orange-200'
  };

  return (
    <>
      {/* Main Insurance Management Dialog */}
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Manage Insurance - {client?.full_name || client?.preferred_name}</DialogTitle>
            <DialogDescription>Add, edit, or archive insurance policies for this client</DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Add Insurance Button */}
            <div className="flex justify-end">
              <Button onClick={handleAddInsuranceClick} className="bg-teal-600 hover:bg-teal-700">
                <Plus className="h-4 w-4 mr-2" />
                Add Insurance
              </Button>
            </div>

            {/* Active Insurances */}
            <div className="space-y-3">
              <h3 className="font-semibold text-lg">Active Insurances</h3>
              {isLoadingInsurances || (loadedForClientId !== client?.id) ? (
                <div className="space-y-3" data-testid="client-insurance-loading">
                  <div className="h-20 bg-gray-100 rounded-md animate-pulse" />
                  <div className="h-20 bg-gray-100 rounded-md animate-pulse" />
                </div>
              ) : clientInsurances.filter(ins => ins.status === 'active').length === 0 ? (
                <p className="text-gray-500 text-center py-8">No active insurances. Click "Add Insurance" to add one.</p>
              ) : (
                <div className="space-y-3">
                  {clientInsurances
                    .filter(ins => ins.status === 'active')
                    .sort((a, b) => a.priority - b.priority)
                    .map((insurance) => {
                      // Coverage-state check — when end_date is past
                      // we surface a loud red banner + outline so the
                      // biller doesn't accidentally bill a DOS the
                      // policy no longer covers.  Local-tz aware so
                      // we don't false-positive on policies that
                      // expire today.
                      const expired = isInsuranceExpired(insurance);
                      const endingSoon = isInsuranceEndingSoon(insurance);
                      return (
                      <div
                        key={insurance.id}
                        className={`border rounded-lg p-4 ${
                          expired
                            ? 'bg-red-50 border-red-300 ring-1 ring-red-300'
                            : priorityColors[insurance.priority] || 'bg-gray-50 border-gray-200'
                        }`}
                        draggable
                        onDragStart={(e) => handleInsuranceDragStart(e, insurance)}
                        onDragOver={handleInsuranceDragOver}
                        onDrop={(e) => handleInsuranceDrop(e, insurance)}
                        data-testid={`client-insurance-card-${insurance.id || insurance.priority}`}
                        data-coverage-state={expired ? 'expired' : endingSoon ? 'ending-soon' : 'active'}
                      >
                        {expired && (
                          <div
                            className="-mt-2 -mx-2 mb-3 px-3 py-2 bg-red-100 border border-red-300 rounded text-red-900 text-xs font-semibold flex items-center gap-2"
                            data-testid={`client-insurance-expired-banner-${insurance.id || insurance.priority}`}
                          >
                            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                            <span>
                              EXPIRED — coverage ended {fmtLocalDate(insurance.end_date)}.  Do not bill DOS after this date on this insurance.
                            </span>
                          </div>
                        )}
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3 flex-1">
                            <GripVertical className="h-5 w-5 text-gray-400 mt-1 cursor-move" />
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <Badge className="bg-white border border-gray-300 text-gray-800 font-semibold">
                                  {priorityLabels[insurance.priority] || `Priority ${insurance.priority}`}
                                </Badge>
                                {/* Shared eligibility badge — same icon/color/label everywhere. */}
                                <EligibilityBadge
                                  status={insurance.eligibility_status || 'not_checked'}
                                  size="sm"
                                  data-testid={`ins-elig-badge-${insurance.priority}`}
                                />
                                {expired && (
                                  <Badge
                                    className="bg-red-600 hover:bg-red-700 text-white font-semibold"
                                    data-testid={`client-insurance-expired-badge-${insurance.id || insurance.priority}`}
                                  >
                                    Expired
                                  </Badge>
                                )}
                                {!expired && endingSoon && (
                                  <Badge
                                    className="bg-amber-500 hover:bg-amber-600 text-white font-medium"
                                    data-testid={`client-insurance-ending-soon-badge-${insurance.id || insurance.priority}`}
                                  >
                                    Ends {fmtLocalDate(insurance.end_date)}
                                  </Badge>
                                )}
                              </div>
                              <h4 className="font-semibold text-lg">{insurance.company}</h4>
                              <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                                {insurance.member_id && (
                                  <p className="text-gray-700"><span className="font-medium">Member ID:</span>{' '}
                                    <CopyableMemberId value={insurance.member_id} testIdSuffix={insurance.id || insurance.priority} />
                                  </p>
                                )}
                                {insurance.group_number && (
                                  <p className="text-gray-700"><span className="font-medium">Group #:</span> {insurance.group_number}</p>
                                )}
                                {insurance.start_date && (
                                  <p className="text-gray-700"><span className="font-medium">Effective:</span> {fmtLocalDate(insurance.start_date)}</p>
                                )}
                                {insurance.end_date && (
                                  <p className={`${expired ? 'text-red-700 font-semibold' : 'text-gray-700'}`}>
                                    <span className="font-medium">Expires:</span> {fmtLocalDate(insurance.end_date)}
                                  </p>
                                )}
                              </div>
                              {/* Alternate IDs preview (May 2026) —
                                  show chips below the main grid so
                                  billers can see other-on-file IDs
                                  without opening the edit form. */}
                              {Array.isArray(insurance.alternate_ids) && insurance.alternate_ids.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`alt-ids-preview-${insurance.id}`}>
                                  {insurance.alternate_ids.map((alt, i) => (
                                    <span
                                      key={alt.id || i}
                                      className="inline-flex items-center gap-1 text-[11px] bg-blue-50 text-blue-800 border border-blue-200 rounded px-1.5 py-0.5 font-mono"
                                      title={`${alt.label || 'Alt ID'}: ${alt.value || ''}`}
                                    >
                                      <span className="font-sans font-medium not-italic">{alt.label || 'Alt'}:</span>
                                      <span>{alt.value}</span>
                                    </span>
                                  ))}
                                </div>
                              )}
                              {Array.isArray(insurance.notes) && insurance.notes.length > 0 && (
                                <p className="mt-1 text-[11px] text-gray-500" data-testid={`note-count-${insurance.id}`}>
                                  {insurance.notes.length} note{insurance.notes.length === 1 ? '' : 's'}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            {(insurance.card_front_url || insurance.card_back_url) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setViewingInsuranceCards({
                                    type: priorityLabels[insurance.priority] || `Priority ${insurance.priority}`,
                                    front: insurance.card_front_url || null,
                                    back: insurance.card_back_url || null
                                  });
                                  setShowInsuranceCardView(true);
                                }}
                              >
                                <CreditCard className="h-4 w-4 mr-1" />
                                View Card
                              </Button>
                            )}
                            {onEligibilityCheck && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onEligibilityCheck(client.id, client.full_name)}
                                className="bg-teal-50 text-teal-700 hover:bg-teal-100"
                              >
                                Check Eligibility
                              </Button>
                            )}
                            {/* Manual verification — for payers where
                                EDI isn't reliable (CCA, Medicaid
                                Renewals, brand-new IDs).  Records
                                what the biller already verified by
                                phone/portal/fax. */}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const levelLabels = {1: 'primary', 2: 'secondary', 3: 'tertiary'};
                                setManualVerifyTarget({
                                  level: levelLabels[insurance.priority] || 'primary',
                                  insuranceName: insurance.insurance_name || insurance.payer_name || '',
                                });
                                setManualVerifyOpen(true);
                              }}
                              className="bg-blue-50 text-blue-700 hover:bg-blue-100"
                              data-testid={`manual-verify-btn-${insurance.id || insurance.priority}`}
                              title="Record manual verification (phone/portal/fax)"
                            >
                              <Phone className="h-3.5 w-3.5 mr-1" />
                              Manual
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditInsuranceClick(insurance)}
                              data-testid={`edit-insurance-${insurance.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setHistoryForInsurance(insurance)}
                              title="View history (archive / reinstate / edits)"
                              data-testid={`view-history-insurance-${insurance.id || insurance.priority}`}
                            >
                              <History className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleArchiveInsurance(insurance)}
                              className="text-red-600 hover:text-red-700"
                              title="Archive (keeps history)"
                              data-testid={`archive-insurance-${insurance.id || insurance.priority}`}
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setDeleteCandidate({ insurance })}
                              className="text-red-700 hover:text-red-900 hover:bg-red-50"
                              title="Delete (only if never billed)"
                              data-testid={`delete-insurance-${insurance.id || insurance.priority}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Archived Insurances */}
            {clientInsurances.filter(ins => ins.status === 'archived').length > 0 && (
              <div className="space-y-3">
                <button
                  onClick={() => setShowArchivedInsurances(!showArchivedInsurances)}
                  className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 font-semibold"
                >
                  {showArchivedInsurances ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  <span>Archived Insurances ({clientInsurances.filter(ins => ins.status === 'archived').length})</span>
                </button>

                {showArchivedInsurances && (
                  <div className="space-y-3">
                    {clientInsurances
                      .filter(ins => ins.status === 'archived')
                      .map((insurance) => (
                        <div
                          key={insurance.id}
                          className="border rounded-lg p-4 bg-gray-100 border-gray-300 opacity-60 grayscale"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <Badge className="bg-gray-200 text-gray-700">Archived</Badge>
                              </div>
                              <h4 className="font-semibold text-lg">{insurance.company}</h4>
                              <div className="grid grid-cols-2 gap-2 mt-2 text-sm text-gray-600">
                                {insurance.member_id && (
                                  <p><span className="font-medium">Member ID:</span>{' '}
                                    <CopyableMemberId value={insurance.member_id} testIdSuffix={`archived-${insurance.id || insurance.priority}`} />
                                  </p>
                                )}
                                {insurance.group_number && (
                                  <p><span className="font-medium">Group #:</span> {insurance.group_number}</p>
                                )}
                                {insurance.start_date && (
                                  <p data-testid={`archived-ins-effective-${insurance.id || insurance.priority}`}>
                                    <span className="font-medium">Effective:</span> {fmtLocalDate(insurance.start_date)}
                                  </p>
                                )}
                                {insurance.end_date && (
                                  <p data-testid={`archived-ins-expires-${insurance.id || insurance.priority}`}>
                                    <span className="font-medium">Expires:</span> {fmtLocalDate(insurance.end_date)}
                                  </p>
                                )}
                                {insurance.archived_at && (
                                  <p>
                                    <span className="font-medium">Archived:</span> {fmtLocalDate(insurance.archived_at)}
                                  </p>
                                )}
                                {insurance.archive_reason && (
                                  <p className="col-span-2"><span className="font-medium">Reason:</span> {insurance.archive_reason}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setHistoryForInsurance(insurance)}
                                title="View history"
                                data-testid={`view-history-archived-${insurance.id || insurance.priority}`}
                              >
                                <History className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReinstateInsurance(insurance)}
                                className="text-green-600 hover:text-green-700"
                              >
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Reinstate
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Client-wide insurance notes (May 2026) — cross-policy
              observations.  Always visible; uses its own endpoint so
              opening the dialog re-fetches the latest. */}
          {client?.id && (
            <div className="mt-6">
              <ClientInsuranceNotesPanel clientId={client.id} onChanged={() => fetchClientInsurances()} />
            </div>
          )}

          <div className="flex justify-end mt-6">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Insurance Card View - Universal Component */}
      <InsuranceCardViewer
        isOpen={showInsuranceCardView}
        onClose={setShowInsuranceCardView}
        cardData={viewingInsuranceCards}
      />

      {/* Manual Verification Dialog — records phone/portal/fax
          verifications without re-running EDI. */}
      {manualVerifyOpen && manualVerifyTarget && (
        <ManualVerifyEligibilityDialog
          open={manualVerifyOpen}
          onOpenChange={(open) => {
            setManualVerifyOpen(open);
            if (!open) setManualVerifyTarget(null);
          }}
          clientId={client?.id}
          level={manualVerifyTarget.level}
          insuranceName={manualVerifyTarget.insuranceName}
          onSuccess={() => {
            // Refresh the client data in the parent so the new
            // verified status surfaces immediately on the chart.
            if (typeof onUpdate === 'function') onUpdate();
          }}
        />
      )}

      {/* Add/Edit Insurance Form Dialog */}
      <Dialog open={showInsuranceForm} onOpenChange={setShowInsuranceForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingClientInsurance ? 'Edit Insurance' : 'Add Insurance'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="company">Insurance Company *</Label>
              <Select
                value={insuranceFormState.company}
                onValueChange={(value) => {
                  const selectedIns = availableInsuranceCompanies.find(ins => ins.name === value);
                  setSelectedInsuranceCompany(selectedIns);
                  setInsuranceFormState(prev => ({
                    ...prev,
                    company: value,
                    plan: '',
                    insurance_id: selectedIns?.id || '',
                    payer_id: selectedIns?.payer_id || '',
                    eligibility_payer_id: selectedIns?.eligibility_payer_id || '',
                  }));
                  setInsuranceSearchTerm('');
                }}
                onOpenChange={(open) => {
                  if (!open) setInsuranceSearchTerm('');
                }}
              >
                <SelectTrigger id="company">
                  <SelectValue placeholder="Search or select insurance company..." />
                </SelectTrigger>
                <SelectContent className="max-h-[400px]">
                  <div className="p-2 sticky top-0 bg-white z-10 border-b">
                    <Input
                      placeholder="Type to search insurances..."
                      value={insuranceSearchTerm}
                      onChange={(e) => setInsuranceSearchTerm(e.target.value)}
                      className="mb-2"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                  </div>
                  {availableInsuranceCompanies && availableInsuranceCompanies.length > 0 ? (
                    <InsuranceCompanyOptions
                      companies={availableInsuranceCompanies}
                      searchTerm={insuranceSearchTerm}
                    />
                  ) : (
                    <div className="p-4 text-center text-gray-500">
                      No insurance companies found. Please contact support.
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Plan Selection */}
            {selectedInsuranceCompany?.plans && selectedInsuranceCompany.plans.length > 0 && (
              <div>
                <Label htmlFor="plan">Insurance Plan</Label>
                <Select
                  value={insuranceFormState.plan}
                  onValueChange={(value) => setInsuranceFormState(prev => ({...prev, plan: value}))}
                >
                  <SelectTrigger id="plan">
                    <SelectValue placeholder="Select plan (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedInsuranceCompany.plans.map((plan, index) => {
                      const planName = typeof plan === 'string' ? plan : plan.name;
                      const planSecId = typeof plan === 'object' ? plan.secondary_identifier : null;
                      return (
                        <SelectItem key={index} value={planName}>
                          {planName}{planSecId ? ` [${planSecId}]` : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="member_id">Member ID *</Label>
                <DebouncedInput
                  id="member_id"
                  value={insuranceFormState.member_id}
                  onChange={(e) => setInsuranceFormState(prev => ({...prev, member_id: e.target.value}))}
                  required
                  data-testid="insurance-member-id-input"
                />
              </div>
              <div>
                <Label htmlFor="group_number">Group Number</Label>
                <DebouncedInput
                  id="group_number"
                  value={insuranceFormState.group_number}
                  onChange={(e) => setInsuranceFormState(prev => ({...prev, group_number: e.target.value}))}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="phone">Insurance Phone</Label>
              <DebouncedInput
                id="phone"
                value={insuranceFormState.phone}
                onChange={(e) => setInsuranceFormState(prev => ({...prev, phone: e.target.value}))}
                placeholder="(XXX) XXX-XXXX"
              />
            </div>

            <div>
              <Label htmlFor="relationship">Relationship to Insured *</Label>
              <Select
                value={insuranceFormState.relationship_to_insured}
                onValueChange={(value) => setInsuranceFormState(prev => ({...prev, relationship_to_insured: value}))}
              >
                <SelectTrigger id="relationship">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">Self</SelectItem>
                  <SelectItem value="spouse">Spouse</SelectItem>
                  <SelectItem value="child">Child</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="insured_name">Insured Person's Name</Label>
              <Input
                id="insured_name"
                value={insuranceFormState.insured_name}
                onChange={(e) => setInsuranceFormState(prev => ({...prev, insured_name: e.target.value}))}
                placeholder={insuranceFormState.relationship_to_insured === 'self' ? 'Client name (auto-filled)' : 'Enter insured person name'}
              />
              <p className="text-xs text-gray-500 mt-1">Leave blank for self-insured</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="insured_ssn">Insured's SSN</Label>
                <DebouncedInput
                  id="insured_ssn"
                  value={insuranceFormState.insured_ssn}
                  onChange={(e) => setInsuranceFormState(prev => ({...prev, insured_ssn: e.target.value}))}
                  placeholder="XXX-XX-XXXX"
                />
              </div>
              <div>
                <Label htmlFor="insured_dob">Insured's Date of Birth</Label>
                <Input
                  id="insured_dob"
                  type="date"
                  value={insuranceFormState.insured_dob}
                  onChange={(e) => setInsuranceFormState(prev => ({...prev, insured_dob: e.target.value}))}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="priority">Priority *</Label>
              <Select
                value={String(insuranceFormState.priority)}
                onValueChange={(value) => setInsuranceFormState(prev => ({...prev, priority: parseInt(value)}))}
              >
                <SelectTrigger id="priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Primary (1)</SelectItem>
                  <SelectItem value="2">Secondary (2)</SelectItem>
                  <SelectItem value="3">Tertiary (3)</SelectItem>
                  <SelectItem value="4">Quaternary (4)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">Change priority to reorder insurances</p>
              {(() => {
                const desired = parseInt(insuranceFormState.priority) || 1;
                const holder = clientInsurances.find(
                  (ins) => ins.status !== 'archived' && ins.id !== editingClientInsurance?.id && (parseInt(ins.priority) || 1) === desired
                );
                if (!holder) return null;
                const lbl = { 1: 'Primary', 2: 'Secondary', 3: 'Tertiary', 4: 'Quaternary' }[desired] || `Priority ${desired}`;
                return (
                  <p className="text-xs text-amber-700 mt-1 flex items-start gap-1" data-testid="priority-collision-hint">
                    <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>{lbl} is currently held by <strong>{holder.company}</strong> — on save you&apos;ll be asked whether to shift the other plan(s) down a rank.</span>
                  </p>
                );
              })()}
            </div>

            {/* Medicare Secondary Payer (MSP) Type Code */}
            {insuranceFormState.company?.toLowerCase().includes('medicare') && insuranceFormState.priority >= 2 && (
              <div className="border-l-4 border-yellow-400 bg-yellow-50 p-4 rounded">
                <div className="space-y-3">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                    <div className="flex-1">
                      <Label htmlFor="msp_type_code" className="text-base font-semibold text-yellow-900">
                        Medicare Secondary Payer (MSP) Type Code *
                      </Label>
                      <p className="text-xs text-yellow-800 mt-1">
                        Required when Medicare is not the primary payer. Select the reason why Medicare is secondary.
                      </p>
                    </div>
                  </div>
                  
                  <Select
                    value={insuranceFormState.msp_type_code || '12'}
                    onValueChange={(value) => setInsuranceFormState(prev => ({...prev, msp_type_code: value}))}
                  >
                    <SelectTrigger id="msp_type_code" className="bg-white h-auto min-h-[40px]">
                      <SelectValue>
                        {(() => {
                          const mspLabels = {
                            '12': 'Working Aged – Beneficiary or spouse with employer group health plan (EGHP)',
                            '13': 'End-Stage Renal Disease (ESRD) – Within 30-month coordination period',
                            '14': 'Auto/No-Fault Insurance – Covers accident-related services first',
                            '15': 'Workers\' Compensation – Work-related illness/injury',
                            '16': 'Other Liability Insurance – Homeowner\'s, malpractice, or general liability',
                            '41': 'Black Lung – Federal Black Lung Program coverage',
                            '42': 'Veterans Administration (VA) – Service-connected condition',
                            '43': 'Public Health Service/Federal Agency – Non-Medicare federal agency',
                            '47': 'Disability – Large Group Health Plan (100+ employees)',
                            '48': 'ESRD + Working Aged/Disability – Multiple entitlements'
                          };
                          const code = insuranceFormState.msp_type_code || '12';
                          return (
                            <div className="text-left py-1">
                              <span className="font-semibold">{code} - </span>
                              <span>{mspLabels[code]}</span>
                            </div>
                          );
                        })()}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="max-h-[400px] w-[600px]">
                      <SelectItem value="12">
                        <div className="py-1 max-w-[560px]">
                          <div className="font-semibold">12 - Working Aged</div>
                          <div className="text-xs text-gray-600 whitespace-normal">Patient/spouse 65+ covered by group health plan through current employment</div>
                        </div>
                      </SelectItem>
                      <SelectItem value="13">
                        <div className="py-1 max-w-[560px]">
                          <div className="font-semibold">13 - End-Stage Renal Disease (ESRD)</div>
                          <div className="text-xs text-gray-600 whitespace-normal">Patient has ESRD within 30-month coordination period</div>
                        </div>
                      </SelectItem>
                      <SelectItem value="14">
                        <div className="py-1 max-w-[560px]">
                          <div className="font-semibold">14 - Auto/No-Fault Insurance</div>
                          <div className="text-xs text-gray-600 whitespace-normal">No-fault or automobile insurance covers accident-related services first</div>
                        </div>
                      </SelectItem>
                      <SelectItem value="15">
                        <div className="py-1 max-w-[560px]">
                          <div className="font-semibold">15 - Workers' Compensation</div>
                          <div className="text-xs text-gray-600 whitespace-normal">Work-related illness/injury - Workers' Comp pays primary</div>
                        </div>
                      </SelectItem>
                      <SelectItem value="16">
                        <div className="py-1 max-w-[560px]">
                          <div className="font-semibold">16 - Other Liability Insurance</div>
                          <div className="text-xs text-gray-600 whitespace-normal">Homeowner's, malpractice, or general liability insurance responsible</div>
                        </div>
                      </SelectItem>
                      <SelectItem value="41">
                        <div className="py-1 max-w-[560px]">
                          <div className="font-semibold">41 - Black Lung</div>
                          <div className="text-xs text-gray-600 whitespace-normal">Patient covered under Federal Black Lung Program</div>
                        </div>
                      </SelectItem>
                      <SelectItem value="42">
                        <div className="py-1 max-w-[560px]">
                          <div className="font-semibold">42 - Veterans Administration (VA)</div>
                          <div className="text-xs text-gray-600 whitespace-normal">Patient entitled to VA benefits for service-connected condition</div>
                        </div>
                      </SelectItem>
                      <SelectItem value="43">
                        <div className="py-1 max-w-[560px]">
                          <div className="font-semibold">43 - Public Health Service/Federal Agency</div>
                          <div className="text-xs text-gray-600 whitespace-normal">Services paid by federal agency other than Medicare</div>
                        </div>
                      </SelectItem>
                      <SelectItem value="47">
                        <div className="py-1 max-w-[560px]">
                          <div className="font-semibold">47 - Disability (Large Group Health Plan)</div>
                          <div className="text-xs text-gray-600 whitespace-normal">Patient under 65, disabled, covered by LGHP (100+ employees)</div>
                        </div>
                      </SelectItem>
                      <SelectItem value="48">
                        <div className="py-1 max-w-[560px]">
                          <div className="font-semibold">48 - ESRD + Working Aged/Disability</div>
                          <div className="text-xs text-gray-600 whitespace-normal">Patient qualifies under both ESRD and another category</div>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date">Effective Date</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={insuranceFormState.start_date}
                  onChange={(e) => setInsuranceFormState(prev => ({...prev, start_date: e.target.value}))}
                />
              </div>
              <div>
                <Label htmlFor="end_date">Termination Date</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={insuranceFormState.end_date}
                  onChange={(e) => setInsuranceFormState(prev => ({...prev, end_date: e.target.value}))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="copay_amount">Client Copay Amount ($)</Label>
                <Input
                  id="copay_amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={insuranceFormState.copay_amount}
                  onChange={(e) => setInsuranceFormState(prev => ({...prev, copay_amount: e.target.value}))}
                  placeholder="0.00"
                />
                <p className="text-xs text-gray-500 mt-1">Client-specific copay (if different from company default)</p>
              </div>
              <div>
                <Label htmlFor="deductible_amount">Client Deductible Amount ($)</Label>
                <Input
                  id="deductible_amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={insuranceFormState.deductible_amount}
                  onChange={(e) => setInsuranceFormState(prev => ({...prev, deductible_amount: e.target.value}))}
                  placeholder="0.00"
                />
                <p className="text-xs text-gray-500 mt-1">Client-specific deductible (if different from company default)</p>
              </div>
            </div>

            {/* Alternate IDs — May 2026 user request.
                Payers like Fallon sometimes issue multiple member IDs;
                billers keep the unused one on file for eligibility
                lookups or member-services calls. Free-form label/value
                so the same shape works for "Old member ID", "Subscriber
                ID", "Medicaid RID", "Auth #", etc. */}
            <div className="space-y-2 border-t pt-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold">Alternate IDs</h4>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="add-alternate-id-btn"
                  onClick={() => setInsuranceFormState(prev => ({
                    ...prev,
                    alternate_ids: [...(prev.alternate_ids || []), { label: '', value: '' }],
                  }))}
                >
                  + Add Alternate ID
                </Button>
              </div>
              {(insuranceFormState.alternate_ids || []).length === 0 ? (
                <p className="text-xs text-gray-500">No alternate IDs on file. Add one if the payer has issued more than one member number for this client.</p>
              ) : (
                <div className="space-y-2">
                  {(insuranceFormState.alternate_ids || []).map((alt, i) => (
                    <div key={alt.id || i} className="flex items-center gap-2" data-testid={`alternate-id-row-${i}`}>
                      <Input
                        placeholder='Label (e.g. "Old Member ID")'
                        value={alt.label || ''}
                        className="flex-1"
                        onChange={(e) => {
                          const next = [...(insuranceFormState.alternate_ids || [])];
                          next[i] = { ...next[i], label: e.target.value };
                          setInsuranceFormState(prev => ({ ...prev, alternate_ids: next }));
                        }}
                        data-testid={`alternate-id-label-${i}`}
                      />
                      <Input
                        placeholder="Value"
                        value={alt.value || ''}
                        className="flex-1"
                        onChange={(e) => {
                          const next = [...(insuranceFormState.alternate_ids || [])];
                          next[i] = { ...next[i], value: e.target.value };
                          setInsuranceFormState(prev => ({ ...prev, alternate_ids: next }));
                        }}
                        data-testid={`alternate-id-value-${i}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => {
                          const next = (insuranceFormState.alternate_ids || []).filter((_, idx) => idx !== i);
                          setInsuranceFormState(prev => ({ ...prev, alternate_ids: next }));
                        }}
                        data-testid={`alternate-id-remove-${i}`}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Per-policy notes — visible only when editing an existing
                insurance (notes need a saved policy to attach to). */}
            {editingClientInsurance?.id && (
              <InsuranceNotesPanel
                clientId={client?.id}
                insuranceId={editingClientInsurance.id}
                onChanged={fetchClientInsurances}
                initialNotes={editingClientInsurance.notes || []}
              />
            )}

            {/* Insurance Card Upload */}
            <div className="space-y-4 border-t pt-4">
              <h4 className="font-semibold">Insurance Card Images</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="card_front">Card Front</Label>
                  <div className="mt-2">
                    <input
                      type="file"
                      id="card_front"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleUploadInsuranceCardFile('front', e.target.files[0]);
                        }
                      }}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                    />
                    {insuranceFormState.card_front_url && (
                      <p className="text-xs text-green-600 mt-1">✓ Uploaded</p>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="card_back">Card Back</Label>
                  <div className="mt-2">
                    <input
                      type="file"
                      id="card_back"
                      accept="image/*"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleUploadInsuranceCardFile('back', e.target.files[0]);
                        }
                      }}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                    />
                    {insuranceFormState.card_back_url && (
                      <p className="text-xs text-green-600 mt-1">✓ Uploaded</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center mt-6">
            {onEligibilityCheck && (
              <Button 
                type="button" 
                variant="outline"
                onClick={() => onEligibilityCheck(client.id, client.full_name)}
                disabled={!insuranceFormState.company || !insuranceFormState.member_id}
                className="bg-teal-50 text-teal-700 hover:bg-teal-100"
              >
                Check Eligibility
              </Button>
            )}
            <div className="flex space-x-2 ml-auto">
              <Button type="button" variant="outline" onClick={() => setShowInsuranceForm(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSaveClientInsurance} 
                className="bg-teal-600 hover:bg-teal-700"
                disabled={!insuranceFormState.company || !insuranceFormState.member_id}
                data-testid="insurance-form-submit"
              >
                {editingClientInsurance ? 'Update Insurance' : 'Add Insurance'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/*
       * Delete-confirmation dialog (Stage: Insurance hard-delete).
       *
       * Two modes:
       *   1. `deleteCandidate.usage` is undefined → first click,
       *      prompt for confirmation.  On confirm we POST DELETE
       *      and either succeed or surface usage counts.
       *   2. `deleteCandidate.usage` is set → the row HAS been
       *      used; show the counts and offer "Archive instead".
       */}
      <Dialog
        open={!!deleteCandidate}
        onOpenChange={(open) => { if (!open) setDeleteCandidate(null); }}
      >
        <DialogContent data-testid="delete-insurance-confirm-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              {deleteCandidate?.usage ? 'Cannot Delete Insurance' : 'Delete Insurance?'}
            </DialogTitle>
            <DialogDescription>
              {deleteCandidate?.usage ? (
                <span>
                  This insurance profile has been used for billing or
                  eligibility checks and cannot be deleted.
                </span>
              ) : (
                <span>
                  This permanently removes <strong>{deleteCandidate?.insurance?.company}</strong>{' '}
                  (Member ID <span className="font-mono">{deleteCandidate?.insurance?.member_id || '—'}</span>) from this chart.
                  This action cannot be undone.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {deleteCandidate?.usage && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm space-y-1.5">
              <div className="font-semibold text-amber-900">Found references:</div>
              <ul className="list-disc pl-5 text-amber-900">
                {deleteCandidate.usage.claims > 0 && (
                  <li><strong>{deleteCandidate.usage.claims}</strong> claim{deleteCandidate.usage.claims === 1 ? '' : 's'}</li>
                )}
                {deleteCandidate.usage.authorizations > 0 && (
                  <li><strong>{deleteCandidate.usage.authorizations}</strong> authorization{deleteCandidate.usage.authorizations === 1 ? '' : 's'}</li>
                )}
                {deleteCandidate.usage.eligibility_records > 0 && (
                  <li><strong>{deleteCandidate.usage.eligibility_records}</strong> eligibility verification{deleteCandidate.usage.eligibility_records === 1 ? '' : 's'}</li>
                )}
              </ul>
              <div className="text-xs text-amber-800 pt-1">
                Archive it instead to preserve billing history.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteCandidate(null)}
              data-testid="delete-insurance-cancel-btn"
            >
              Cancel
            </Button>
            {deleteCandidate?.usage ? (
              <Button
                variant="outline"
                onClick={async () => {
                  const ins = deleteCandidate.insurance;
                  setDeleteCandidate(null);
                  await handleArchiveInsurance(ins);
                }}
                data-testid="delete-insurance-archive-instead-btn"
                className="bg-amber-50 text-amber-900 hover:bg-amber-100 border-amber-300"
              >
                <Archive className="h-4 w-4 mr-1.5" />
                Archive Instead
              </Button>
            ) : (
              <Button
                onClick={() => handleDeleteInsurance(deleteCandidate.insurance)}
                data-testid="delete-insurance-confirm-btn"
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Delete Permanently
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* -- Rank-cascade confirmation (Jun 2026) --
       *
       * Shown when the saved priority collides with an existing active
       * policy.  Previews exactly which plans shift down a rank; the
       * user can cascade, keep ranks as entered, or cancel. */}
      <Dialog
        open={!!cascadePrompt}
        onOpenChange={(open) => { if (!open) setCascadePrompt(null); }}
      >
        <DialogContent className="max-w-xl" data-testid="cascade-priority-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              Shift Other Insurance Ranks?
            </DialogTitle>
            <DialogDescription>
              {(() => {
                const lbl = { 1: 'Primary', 2: 'Secondary', 3: 'Tertiary', 4: 'Quaternary' };
                const desired = parseInt(insuranceFormState.priority) || 1;
                return `Another active policy already holds the ${lbl[desired] || `Priority ${desired}`} rank. Shift the plan(s) below down one rank to make room?`;
              })()}
            </DialogDescription>
          </DialogHeader>
          {cascadePrompt?.shifts?.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-1.5" data-testid="cascade-shift-preview">
              {cascadePrompt.shifts.map((s) => {
                const lbl = { 1: 'Primary', 2: 'Secondary', 3: 'Tertiary', 4: 'Quaternary' };
                return (
                  <div key={s.id} className="text-sm text-amber-900 flex items-center gap-2">
                    <span className="font-semibold">{s.company}:</span>
                    <span>{lbl[s.from] || `Priority ${s.from}`}</span>
                    <span aria-hidden>→</span>
                    <span className="font-medium">{lbl[s.to] || `Priority ${s.to}`}</span>
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              className="w-full sm:w-auto"
              onClick={() => setCascadePrompt(null)}
              data-testid="cascade-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              className="w-full sm:w-auto whitespace-nowrap"
              onClick={() => performSaveClientInsurance(false)}
              data-testid="cascade-decline-btn"
            >
              No, save as entered
            </Button>
            <Button
              className="w-full sm:w-auto whitespace-nowrap bg-teal-600 hover:bg-teal-700 text-white"
              onClick={() => performSaveClientInsurance(true)}
              data-testid="cascade-confirm-btn"
            >
              Yes, shift others down
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* -- Insurance History dialog --
       *
       * Surfaces the full `history` array (every archive / reinstate /
       * edit / merge event with actor + timestamp).  Read-only audit
       * trail.  Closes by clicking outside or pressing Escape. */}
      <Dialog
        open={!!historyForInsurance}
        onOpenChange={(open) => {
          if (!open) setHistoryForInsurance(null);
        }}
      >
        <DialogContent
          className="max-w-2xl max-h-[80vh] overflow-y-auto"
          data-testid="insurance-history-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-4 w-4 text-teal-600" />
              Insurance History
            </DialogTitle>
          </DialogHeader>

          {historyForInsurance && (
            <>
              <div className="mb-3 text-sm">
                <div className="font-semibold text-slate-800">
                  {historyForInsurance.company}
                </div>
                <div className="text-xs text-slate-500">
                  {historyForInsurance.member_id ? `Member ID: ${historyForInsurance.member_id} · ` : ''}
                  {historyForInsurance.start_date
                    ? `Effective ${fmtLocalDate(historyForInsurance.start_date)}`
                    : ''}
                  {historyForInsurance.end_date
                    ? ` → ${fmtLocalDate(historyForInsurance.end_date)}`
                    : ''}
                </div>
              </div>

              {(() => {
                const events = (historyForInsurance.history || [])
                  .slice()
                  .sort((a, b) => {
                    // Newest first.  Falls back to insertion order if
                    // timestamps are missing.
                    const ta = a?.timestamp ? new Date(a.timestamp).getTime() : 0;
                    const tb = b?.timestamp ? new Date(b.timestamp).getTime() : 0;
                    return tb - ta;
                  });

                if (events.length === 0) {
                  return (
                    <div
                      className="text-center py-8 text-sm text-slate-400 italic"
                      data-testid="insurance-history-empty"
                    >
                      No history entries yet.
                    </div>
                  );
                }

                return (
                  <ul
                    className="space-y-2"
                    data-testid="insurance-history-list"
                  >
                    {events.map((evt, idx) => (
                      <li
                        key={idx}
                        className="border rounded-md p-3 bg-slate-50 text-sm"
                        data-testid={`insurance-history-entry-${idx}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold capitalize text-slate-800">
                            {evt.action || 'event'}
                          </span>
                          <span className="text-xs text-slate-500">
                            {evt.timestamp
                              ? new Date(evt.timestamp).toLocaleString()
                              : '—'}
                          </span>
                        </div>
                        {(evt.changed_by_name || evt.changed_by) && (
                          <div className="text-xs text-slate-600 mt-0.5">
                            by{' '}
                            <span className="font-medium">
                              {evt.changed_by_name || evt.changed_by}
                            </span>
                          </div>
                        )}
                        {(evt.old_priority || evt.new_priority) && (
                          <div className="text-xs text-slate-600 mt-0.5">
                            priority {evt.old_priority ?? '—'} →{' '}
                            {evt.new_priority ?? '—'}
                          </div>
                        )}
                        {evt.details && (
                          <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">
                            {typeof evt.details === 'object'
                              ? JSON.stringify(evt.details, null, 2)
                              : String(evt.details)}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ClientInsuranceDialog;
